import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createNoise2D } from 'simplex-noise';
import { 
    CHUNK_SIZE, 
    MAP_RADIUS, 
    WORLD_SIZE, 
    ISLAND_RADIUS, 
    getActiveSeed, 
    ISLAND_SHAPES, 
    getIslandShapeFromURL,
    ISLAND_TYPE_SETTINGS,
    ISLAND_SHAPE_NAMES
} from './TerrainConstants.js';
import { 
    WATER_LEVEL, 
    BASE_HEIGHT, 
    WORLD_CHUNKS_ACROSS, 
    WATER_BORDER, 
    MOUNTAIN_HEIGHT,
    DEFAULT_TERRAIN_SETTINGS, 
    MAX_HEIGHT_VARIATION
} from '../terrain/TerrainConstants.js';
import { smoothHeightMap, createHeightMap } from './TerrainUtils.js';
import { TerrainCollisionManager } from '../collision/TerrainCollisionManager.js';
import IslandTypeManager from './IslandTypeManager.js';

// Create shared geometries
const VOXEL_GEOMETRY = new THREE.BoxGeometry(1, 1, 1);
const WATER_GEOMETRY = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE);

// Biome constants
const BIOME_OCEAN = 'ocean';
const BIOME_DEEP_OCEAN = 'deep_ocean';
const BIOME_SHORE = 'shore';
const BIOME_PLAINS = 'plains';
const BIOME_HILLS = 'hills';
const BIOME_MOUNTAINS = 'mountains';
const BIOME_VOLCANIC = 'volcanic';
const BIOME_LAGOON = 'lagoon';
const BIOME_REEF = 'reef';
const BIOME_CLIFF = 'cliff';

// Note: We're using the ISLAND_SHAPES imported from TerrainConstants.js

// Add the getIslandShape function back
function getIslandShape(seed) {
    // Check if a shape was explicitly selected in URL
    const selectedShape = getIslandShapeFromURL();
    if (selectedShape !== null) {
        return selectedShape;
    }
    
    // Otherwise use the random selection based on seed
    const hash = Math.sin(seed * 747382.349) * 10000;
    const normalizedHash = Math.abs(hash) % 1;
    
    if (normalizedHash < 0.2) return ISLAND_SHAPES.CIRCULAR;
    if (normalizedHash < 0.4) return ISLAND_SHAPES.IRREGULAR;
    if (normalizedHash < 0.6) return ISLAND_SHAPES.CRESCENT;
    if (normalizedHash < 0.8) return ISLAND_SHAPES.VOLCANIC;
    if (normalizedHash < 0.9) return ISLAND_SHAPES.ATOLL;
    return ISLAND_SHAPES.ARCHIPELAGO;
}

// Create seeded noise generator
function createSeededNoise2D(seed) {
    // Use alea algorithm for seeded PRNG
    const alea = function(seed) {
        let s0 = 0;
        let s1 = 0;
        let s2 = 0;
        let c = 1;
        
        const mash = function(data) {
            data = data.toString();
            for (let i = 0; i < data.length; i++) {
                c += data.charCodeAt(i);
                s0 += c;
                s1 += c;
                s2 += c;
                s0 %= 2147483647;
                s1 %= 2147483647;
                s2 %= 2147483647;
                c %= 2147483647;
            }
            return s0 / 2147483647;
        };
        
        if (seed === undefined) {
            seed = Math.random() * 2147483647;
        }
        
        mash(' ');
        mash(' ');
        mash(' ');
        mash(seed);
        
        return function() {
            const t = 2091639 * s0 + c * 2.3283064365386963e-10;
            s0 = s1;
            s1 = s2;
            c = t | 0;
            s2 = t - c;
            return s2;
        };
    };
    
    // Create seeded PRNG
    const random = alea(seed);
    
    // Create a seeded noise function
    return createNoise2D(random);
}

// Use world seed from global variable
function createNoiseGenerator() {
    // Ensure we have a valid worldSeed
    if (!window.worldSeed) {
        console.warn('No worldSeed found in window.worldSeed, checking currentGameSeed...');
        
        if (window.currentGameSeed) {
            window.worldSeed = window.currentGameSeed;
            console.log(`Using currentGameSeed as worldSeed: ${window.worldSeed}`);
        } else {
            console.warn('No seed found in either location, using default');
            window.worldSeed = getActiveSeed();
        }
    }
    
    // Log the final seed being used
    console.log(`[TerrainChunk] Creating noise generator with seed: ${window.worldSeed}`);
    return createSeededNoise2D(window.worldSeed);
}

// Create the noise generator using the global seed
const noise2D = createNoiseGenerator();

// We'll use multiple noise octaves for more natural terrain
function ridgedNoise(x, z, octaves = 6, falloff = 0.5, scale = 1.0) {
    let noise = 0;
    let amp = 1.0;
    let freq = scale;
    let totalAmp = 0;
    
    for (let i = 0; i < octaves; i++) {
        // Get absolute value of noise and invert it to create ridges
        const n = 1 - Math.abs(noise2D(x * freq, z * freq));
        // Square it for sharper ridges
        noise += n * n * amp;
        totalAmp += amp;
        amp *= falloff;
        freq *= 2;
    }
    
    return noise / totalAmp;
}

function terraceNoise(x, z, levels = 10, hardness = 2.0) {
    const noise = ridgedNoise(x, z);
    return Math.pow(Math.round(noise * levels) / levels, hardness);
}

function crateredNoise(x, z, craterRadius, craterDepth) {
    const dist = Math.sqrt(x*x + z*z);
    const craterEffect = Math.max(0, 1 - Math.abs(dist - craterRadius) / (craterRadius * 0.2));
    return -craterDepth * craterEffect;
}

// Mountain generation parameters
const MOUNTAIN_RANGES = 4; // Reduced number of mountain ranges
const MOUNTAIN_RANGE_SIZE = 0.15; // Reduced size of mountain ranges
const MOUNTAIN_RANGE_HEIGHT = 1.5; // Reduced height multiplier for more natural mountains

// Standalone biome generation function
export function getBiomeAt(x, z, height, distance, mountainRanges = [], frequency = 0.02) {
    // Use the global world seed directly
    const worldSeed = window.worldSeed;
    const islandShape = getIslandShape(worldSeed);
    
    // Add more variation to distance calculation based on island shape
    let adjustedDistance = distance;
    
    // Apply noise variation based on island shape
    const noiseScale = 0.01;
    const distanceNoise = noise2D(x * noiseScale, z * noiseScale) * 0.15;
    
    // For irregular islands, apply more significant noise
    if (islandShape === ISLAND_SHAPES.IRREGULAR) {
        adjustedDistance += distanceNoise * 0.3;
    }
    // For crescent islands, create a crescent shape
    else if (islandShape === ISLAND_SHAPES.CRESCENT) {
        const angle = Math.atan2(z, x);
        const crescentFactor = Math.cos(angle) * 0.2;
        adjustedDistance -= crescentFactor;
    }
    // For volcanic islands, keep mostly circular but with some irregularity
    else if (islandShape === ISLAND_SHAPES.VOLCANIC) {
        adjustedDistance += distanceNoise * 0.1;
    }
    // For archipelago, create multiple smaller islands
    else if (islandShape === ISLAND_SHAPES.ARCHIPELAGO) {
        // Use noise to create multiple centers
        const archipelagoNoise = noise2D(x * 0.02 + 500, z * 0.02 + 500) * 0.5;
        
        // Create channels to connect isolated water bodies to the ocean
        const channelNoiseX = noise2D(x * 0.01 + 1000, z * 0.01 + 1000);
        const channelNoiseZ = noise2D(x * 0.01 + 2000, z * 0.01 + 2000);
        
        // If we're in a potential depression/hole area, create water channels
        if (archipelagoNoise > 0.2 && archipelagoNoise < 0.3) {
            // Create water channels in certain noise ranges to connect holes to ocean
            if (channelNoiseX > 0.6 || channelNoiseZ > 0.6) {
                adjustedDistance += 0.3; // Push more towards ocean to create a channel
            }
        }
        
        // Apply the basic archipelago noise
        adjustedDistance += archipelagoNoise;
    }
    else {
        // Default circular with subtle noise
        adjustedDistance += distanceNoise * 0.1;
    }
    
    // Ensure a clear boundary between land and water
    const waterBoundary = 0.95;
    
    // Determine biome based on adjusted distance and additional factors
    if (adjustedDistance > waterBoundary) return BIOME_DEEP_OCEAN;
    if (adjustedDistance > 0.9) return BIOME_OCEAN;
    
    // Shore zones with clearer transition
    if (adjustedDistance > 0.8) {
        // Create occasional cliff areas along shore
        const cliffNoise = noise2D(x * 0.05 + 1000, z * 0.05 + 1000);
        if (cliffNoise > 0.6 && islandShape !== ISLAND_SHAPES.ATOLL) {
            return BIOME_CLIFF;
        }
        return BIOME_SHORE;
    }
    
    // Mountain and volcanic regions
    const centerDist = Math.sqrt(x*x + z*z) / ISLAND_RADIUS;
    const mountainNoise = ridgedNoise(x * 0.005 + 1000, z * 0.005 + 1000, 4, 0.6, 1);
    
    // For volcanic islands, create a central volcano
    if (islandShape === ISLAND_SHAPES.VOLCANIC && centerDist < 0.3) {
        return BIOME_VOLCANIC;
    }
    
    // Mountain determination with more variation
    if ((mountainNoise > 0.6 || (centerDist < 0.3 && mountainNoise > 0.4)) && 
        islandShape !== ISLAND_SHAPES.ATOLL) {
        return BIOME_MOUNTAINS;
    }
    
    // Hills with more natural distribution
    const hillNoise = noise2D(x * 0.01 + 2000, z * 0.01 + 2000);
    if (hillNoise > 0.3 || (adjustedDistance < 0.7 && adjustedDistance > 0.5)) {
        return BIOME_HILLS;
    }
    
    // Default to plains
    return BIOME_PLAINS;
}

// Add constants for chunk blending
const CHUNK_BLEND_MARGIN = 2; // Margin for blending between chunks
const CHUNK_BLEND_STRENGTH = 0.4; // How strong the blending should be

export class TerrainChunk {
    constructor(x, y, settings, materials, neighborHeightData = null) {
        // Add initialization guard with better error handling
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            console.error('Invalid chunk coordinates:', x, y);
            throw new Error('Invalid chunk coordinates');
        }

        // Store chunk coordinates for debugging
        this.chunkX = x;
        this.chunkY = y;
        
        // Initialize position with validation
        this.position = new THREE.Vector2(x, y);
        if (!this.position) {
            console.error('Failed to create chunk position');
            throw new Error('Failed to create chunk position');
        }

        // Store neighbor data for boundary blending
        this.neighborHeightData = neighborHeightData;

        // Important: Get defaults directly from TerrainConstants to ensure consistency
        // Explicitly using DEFAULT_TERRAIN_SETTINGS for critical values
        this.settings = {
            amplitude: DEFAULT_TERRAIN_SETTINGS.amplitude,
            frequency: DEFAULT_TERRAIN_SETTINGS.frequency || 0.02,
            baseHeight: DEFAULT_TERRAIN_SETTINGS.baseHeight || BASE_HEIGHT,
            variation: DEFAULT_TERRAIN_SETTINGS.variation || MAX_HEIGHT_VARIATION,
            scale: DEFAULT_TERRAIN_SETTINGS.scale || 1,
            smoothness: DEFAULT_TERRAIN_SETTINGS.smoothness || 0.7,
            edgeSharpness: DEFAULT_TERRAIN_SETTINGS.edgeSharpness || 1.2,
            noiseScale: DEFAULT_TERRAIN_SETTINGS.noiseScale || 0.04,
            // Use default island type if not provided
            islandType: 0
        };

        // Apply any custom settings (but maintain defaults from constants)
        if (settings) {
            Object.assign(this.settings, settings);
        }

        // Important: Log the actual settings being used
        console.log(`[TerrainChunk] Using settings:`, this.settings);

        // Ensure materials are provided
        if (!materials) {
            console.error('No materials provided for chunk');
            throw new Error('No materials provided for chunk');
        }
        this.materials = materials;

        // Initialize collections
        this.voxels = new Map();
        this.mesh = null;
        this.waterMesh = null;
        this.physicsBody = null;
        this.heightMap = null; // Store the final heightmap for sharing with neighbors
        
        // Add flags to track processing status
        this.smoothingApplied = false;
        this.archipelagoProcessed = false;

        // Initialize world seed if not already set
        if (!window.worldSeed) {
            window.worldSeed = getActiveSeed();
        }

        // Determine island shape to use - prefer what's in settings if available
        let selectedShape;
        if (settings && 'islandType' in settings && settings.islandType >= 0 && settings.islandType <= 5) {
            // Use the island type provided in settings
            selectedShape = settings.islandType;
            console.log(`[TerrainChunk] Using island shape from settings: ${ISLAND_SHAPE_NAMES[selectedShape]} (${selectedShape})`);
        } else {
            // Get island type from manager
            selectedShape = IslandTypeManager.getInstance().getType();
            console.log(`[TerrainChunk] Using island shape from manager: ${ISLAND_SHAPE_NAMES[selectedShape]} (${selectedShape})`);
        }

        // Store the island type for later use
        this.islandType = selectedShape;
        
        // Get terrain settings for the selected shape
        const terrainSettings = ISLAND_TYPE_SETTINGS[selectedShape] || DEFAULT_TERRAIN_SETTINGS;

        // Generate terrain with error handling
        try {
            console.log(`Generating chunk at (${x}, ${y})`);
            this.generateTerrain();
            this.createWater();
            console.log(`Successfully generated chunk at (${x}, ${y})`);
        } catch (error) {
            console.error(`Error generating chunk at (${x}, ${y}):`, error);
            throw error;
        }
    }

    createWater() {
        // Only create water for chunks near the coast or in the ocean
        const worldPosX = this.position.x * CHUNK_SIZE - MAP_RADIUS;
        const worldPosZ = this.position.y * CHUNK_SIZE - MAP_RADIUS;
        const distanceFromCenter = Math.sqrt(worldPosX * worldPosX + worldPosZ * worldPosZ);
        
        // Create water for all areas except the very center of the island
        if (distanceFromCenter > ISLAND_RADIUS - CHUNK_SIZE * 4 || 
            distanceFromCenter > ISLAND_RADIUS * 0.6) {
            const waterGeometry = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE);
            const waterMaterial = new THREE.MeshStandardMaterial({
                color: 0x0077be,
                transparent: true,
                opacity: 0.6,
                side: THREE.DoubleSide
            });
            this.waterMesh = new THREE.Mesh(waterGeometry, waterMaterial);
            this.waterMesh.rotation.x = -Math.PI / 2;
            this.waterMesh.position.set(
                this.position.x * CHUNK_SIZE + CHUNK_SIZE / 2,
                WATER_LEVEL,
                this.position.y * CHUNK_SIZE + CHUNK_SIZE / 2
            );
        }
    }

    generateTerrain() {
        if (!this.position) {
            console.error('No position set for chunk');
            return;
        }

        console.log(`Starting terrain generation for chunk (${this.chunkX}, ${this.chunkY})`);
        
        // Create an extended heightmap with margins for blending with neighboring chunks
        // This allows us to generate heights slightly beyond our chunk borders for smoother transitions
        const extendedSize = CHUNK_SIZE + 2 * CHUNK_BLEND_MARGIN;
        let heightMap = new Array(extendedSize).fill(0)
            .map(() => new Array(extendedSize).fill(0));

        const worldX = this.position.x;
        const worldZ = this.position.y;
        const chunkMinX = worldX * CHUNK_SIZE - MAP_RADIUS;
        const chunkMinZ = worldZ * CHUNK_SIZE - MAP_RADIUS;
        
        try {
            // Generate initial height map with margin for blending
            for (let x = 0; x < extendedSize; x++) {
                for (let z = 0; z < extendedSize; z++) {
                    // Convert to world coordinates, accounting for margin
                    const pointX = chunkMinX + x - CHUNK_BLEND_MARGIN;
                    const pointZ = chunkMinZ + z - CHUNK_BLEND_MARGIN;
                    const distanceFromCenter = Math.sqrt(pointX * pointX + pointZ * pointZ);
                    
                    // Generate height with validation
                    let height = this.calculateHeight(x - CHUNK_BLEND_MARGIN, z - CHUNK_BLEND_MARGIN, distanceFromCenter);
                    if (!isFinite(height)) {
                        console.warn(`Invalid height generated at (${x}, ${z}), using default`);
                        height = WATER_LEVEL;
                    }
                    
                    heightMap[x][z] = height;
                }
            }
            
            try {
                // Track if this chunk has already been through smoothing
                if (this.smoothingApplied) {
                    // Skip smoothing if already applied
                    console.debug(`Skipping smoothing for already processed chunk (${this.chunkX}, ${this.chunkY})`);
                } else {
                    // Apply a series of safer smoothing passes with error handling
                    // First smoothing pass with small radius (no console.log to reduce lag)
                    heightMap = smoothHeightMap(heightMap, 1.2);
                    
                    // Check if we're in the shore/coast region where we need more smoothing
                    const distanceFromCenter = Math.sqrt(
                        (worldX * CHUNK_SIZE + CHUNK_SIZE/2 - MAP_RADIUS)**2 + 
                        (worldZ * CHUNK_SIZE + CHUNK_SIZE/2 - MAP_RADIUS)**2
                    ) / ISLAND_RADIUS;
                    
                    // Apply second pass with medium radius for general terrain smoothing
                    try {
                        heightMap = smoothHeightMap(heightMap, 1.5);
                    } catch (smoothError) {
                        console.error(`Error in second smoothing pass: ${smoothError}`);
                        // Continue with existing heightMap
                    }
                    
                    // Optimize coastal smoothing - only apply most intensive smoothing in coastal areas
                    if (distanceFromCenter > 0.75) {
                        // Apply more aggressive smoothing to coastal areas (removed console.log)
                        try {
                            heightMap = smoothHeightMap(heightMap, 2.5);
                            
                            // Only apply final smoothing at a lower radius to reduce computation
                            // while preserving terrain shape
                            heightMap = smoothHeightMap(heightMap, 2.5);
                        } catch (smoothError) {
                            console.error(`Error in coastal smoothing pass: ${smoothError}`);
                        }
                    } else if (distanceFromCenter > 0.5) {
                        // Medium distance from center - apply intermediate smoothing
                        try {
                            heightMap = smoothHeightMap(heightMap, 2.0);
                        } catch (smoothError) {
                            console.error(`Error in medium-distance smoothing pass: ${smoothError}`);
                        }
                    } else {
                        // Central area - apply moderate smoothing to preserve some terrain features
                        try {
                            heightMap = smoothHeightMap(heightMap, 1.8);
                        } catch (smoothError) {
                            console.error(`Error in central area smoothing pass: ${smoothError}`);
                        }
                    }
                    
                    // Apply a focused edge smoothing pass for chunk boundaries
                    try {
                        heightMap = this.smoothChunkBoundaries(heightMap);
                    } catch (error) {
                        console.error(`Error in chunk boundary smoothing: ${error}`);
                    }
                    
                    // Mark this chunk as having smoothing applied to prevent redundant operations
                    this.smoothingApplied = true;
                }
            } catch (smoothingError) {
                console.error(`Smoothing error for chunk (${this.chunkX}, ${this.chunkY}): ${smoothingError}`);
                // Continue with the unsmoothed heightMap
            }
            
            // Apply slope limiting for walkable terrain with error handling
            try {
                heightMap = this.limitSteepSlopes(heightMap);
            } catch (slopeError) {
                console.error(`Error in slope limiting: ${slopeError}`);
                // Continue with the existing heightMap
            }
            
            // Archipelago-specific post-processing only if not already processed
            if (this.islandType === ISLAND_SHAPES.ARCHIPELAGO && !this.archipelagoProcessed) {
                try {
                    // Remove console.log to reduce lag
                    // Apply water channel carving for archipelago islands
                    for (let x = CHUNK_BLEND_MARGIN; x < heightMap.length - CHUNK_BLEND_MARGIN; x++) {
                        for (let z = CHUNK_BLEND_MARGIN; z < heightMap[x].length - CHUNK_BLEND_MARGIN; z++) {
                            const worldX = this.position.x * CHUNK_SIZE + (x - CHUNK_BLEND_MARGIN);
                            const worldZ = this.position.y * CHUNK_SIZE + (z - CHUNK_BLEND_MARGIN);
                            
                            // Detect potential isolated water areas
                            const channelNoise = noise2D(worldX * 0.01 + 2500, worldZ * 0.01 + 2500);
                            const isChannelArea = channelNoise > 0.65;
                            
                            // If we're in a potential isolated area and height is near water level, carve a channel
                            if (isChannelArea && heightMap[x][z] > WATER_LEVEL - 2 && heightMap[x][z] < WATER_LEVEL + 3) {
                                // Set water height consistently to prevent collision issues
                                const waterChannelHeight = WATER_LEVEL - 1.5;
                                heightMap[x][z] = waterChannelHeight;
                                
                                // Register this height with collision manager to ensure consistent collision
                                if (window.terrainCollision) {
                                    window.terrainCollision.setBlockHeight(worldX, worldZ, waterChannelHeight);
                                }
                                
                                // Also affect neighboring cells to create wider channels
                                const radius = 2;
                                for (let nx = Math.max(CHUNK_BLEND_MARGIN, x-radius); nx <= Math.min(heightMap.length-1-CHUNK_BLEND_MARGIN, x+radius); nx++) {
                                    for (let nz = Math.max(CHUNK_BLEND_MARGIN, z-radius); nz <= Math.min(heightMap[x].length-1-CHUNK_BLEND_MARGIN, z+radius); nz++) {
                                        // Calculate distance from center cell
                                        const dist = Math.sqrt((nx-x)**2 + (nz-z)**2);
                                        if (dist <= radius) {
                                            // Apply a gradual slope effect for the channel
                                            const channelEffect = 1 - (dist / radius);
                                            const targetHeight = waterChannelHeight + (dist * 0.5);
                                            // Only modify if we're lowering the height
                                            if (heightMap[nx][nz] > targetHeight) {
                                                heightMap[nx][nz] = targetHeight;
                                                
                                                // Register neighbor height with collision manager as well
                                                const neighborX = this.position.x * CHUNK_SIZE + (nx - CHUNK_BLEND_MARGIN);
                                                const neighborZ = this.position.y * CHUNK_SIZE + (nz - CHUNK_BLEND_MARGIN);
                                                if (window.terrainCollision) {
                                                    window.terrainCollision.setBlockHeight(neighborX, neighborZ, targetHeight);
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    
                    // Mark archipelago processing as complete
                    this.archipelagoProcessed = true;
                } catch (error) {
                    console.error(`Error in archipelago post-processing: ${error}`);
                }
            }
            
            // Create the chunk heightmap by discarding the margin
            let finalHeightMap = new Array(CHUNK_SIZE + 1).fill(0)
                .map(() => new Array(CHUNK_SIZE + 1).fill(0));
            
            // Copy the blended heights to the final heightmap without the margins
            for (let x = 0; x <= CHUNK_SIZE; x++) {
                for (let z = 0; z <= CHUNK_SIZE; z++) {
                    finalHeightMap[x][z] = heightMap[x + CHUNK_BLEND_MARGIN][z + CHUNK_BLEND_MARGIN];
                }
            }
            
            // Apply neighbor blending if we have neighbor data
            if (this.neighborHeightData) {
                try {
                    finalHeightMap = this.blendWithNeighbors(finalHeightMap);
                } catch (error) {
                    console.error(`Error blending with neighbors: ${error}`);
                    // Continue with unblended heightmap
                }
            }
            
            // Store the final heightmap for sharing with other chunks
            this.heightMap = finalHeightMap;
            
            // Replace console.log with console.debug to reduce visible logs
            console.debug(`Height map processed for chunk (${this.chunkX}, ${this.chunkY})`);
            this.generateVoxels(finalHeightMap);
            this.createPhysics(finalHeightMap);
            
        } catch (error) {
            console.error(`Error in terrain generation for chunk (${this.chunkX}, ${this.chunkY}):`, error);
            throw error;
        }
    }

    calculateHeight(x, z, distanceFromCenter) {
        // Get world coordinates for this chunk
        const worldX = this.position.x * CHUNK_SIZE + x;
        const worldZ = this.position.y * CHUNK_SIZE + z;
        
        // Check if this is the spawn chunk (0,0) - create a flat safe area
        if (this.position.x === 0 && this.position.y === 0) {
            // Create a flat area at the center of the starter chunk
            const chunkCenterX = CHUNK_SIZE / 2;
            const chunkCenterZ = CHUNK_SIZE / 2;
            
            // Calculate distance from chunk center
            const distFromChunkCenter = Math.sqrt(
                Math.pow(x - chunkCenterX, 2) + 
                Math.pow(z - chunkCenterZ, 2)
            );
            
            // Flat plateau with slight randomness based on world seed
            const flatHeight = 8 + ((window.worldSeed % 5) / 10);
            
            // Create a circular flat area in the center of the chunk
            if (distFromChunkCenter < CHUNK_SIZE / 2) {
                // Inside the flat area
                return flatHeight;
            } else {
                // Transition zone - smooth blend to normal terrain
                const blend = Math.min(1, (distFromChunkCenter - CHUNK_SIZE / 2) / 10);
                const normalHeight = this.calculateNormalHeight(worldX, worldZ, distanceFromCenter);
                return flatHeight * (1 - blend) + normalHeight * blend;
            }
        }
        
        // For non-spawn chunks, use normal height calculation
        return this.calculateNormalHeight(worldX, worldZ, distanceFromCenter);
    }
    
    // Original height calculation logic moved to a separate method
    calculateNormalHeight(worldX, worldZ, distanceFromCenter) {
        // Use the global world seed directly
        const worldSeed = window.worldSeed;
        // Use the stored island type instead of getting it again
        const islandShape = this.islandType !== undefined ? this.islandType : getIslandShape(worldSeed);
        
        // Calculate distance from center for island shape
        const maxRadius = ISLAND_RADIUS;
        const distance = distanceFromCenter / maxRadius;
        
        // Get noise coordinates for biome determination
        const wx = worldX * this.settings.frequency;
        const wz = worldZ * this.settings.frequency;
        
        // Determine biome
        const biome = this.getBiome(distance, 0, worldX, worldZ);
        
        // Calculate base height using advanced noise functions
        let height = this.calculateBaseHeight(worldX, worldZ, biome, islandShape);
        
        // Apply specific island shape transformations
        const angle = Math.atan2(worldZ, worldX);
        const normalizedDist = distance * 2 - 1; // -1 to 1 range
        
        // Shape specific modifications
        switch(islandShape) {
            case ISLAND_SHAPES.VOLCANIC:
                // Create central volcanic mountain with more dramatic height
                if (distance < 0.4) {
                    const volcanicPeak = 60 + ridgedNoise(worldX * 0.01, worldZ * 0.01) * 40;
                    const volcanicHeight = volcanicPeak * (1 - distance / 0.4);
                    
                    // Create crater with reduced depth
                    const craterEffect = crateredNoise(worldX, worldZ, ISLAND_RADIUS * 0.15, 15);
                    
                    // Blend heights
                    height = Math.max(height, volcanicHeight + craterEffect);
                }
                break;
                
            case ISLAND_SHAPES.CRESCENT:
                // Create crescent shape with more dramatic elevation changes
                const crescentFactor = Math.cos(angle) * 0.25;
                const crescentSide = Math.cos(angle) > 0;
                
                if (crescentSide && distance < 0.8) {
                    // More dramatic elevation increase on this side
                    height *= 1.5;
                }
                break;
                
            case ISLAND_SHAPES.ARCHIPELAGO:
                // Create multiple elevation centers using noise with moderate heights
                const islandNoise = ridgedNoise(worldX * 0.005 + 500, worldZ * 0.005 + 500, 3, 0.7);
                
                // Detect potential isolated depressions in archipelago terrain
                const depressionCheck = noise2D(worldX * 0.01 + 1500, worldZ * 0.01 + 1500);
                const isDepressionArea = depressionCheck > 0.4 && depressionCheck < 0.6;
                
                if (islandNoise > 0.6 && distance < 0.8) {
                    // Create higher areas where noise is strong - but less extreme heights
                    const heightBoost = 25 * (islandNoise - 0.6) / 0.4;
                    
                    // If in a potential depression area, raise the floor to prevent deep pits
                    if (isDepressionArea) {
                        const minHeight = WATER_LEVEL + 3; // Ensure minimum height above water
                        height = Math.max(height, minHeight);
                        height += heightBoost * 0.5; // Apply less height variation
                    } else {
                        height += heightBoost;
                    }
                } else if (isDepressionArea && height < WATER_LEVEL + 2) {
                    // For areas that might become isolated pits or depressions
                    // Either make them fully underwater (for consistent collision)
                    // or raise them above water level (to prevent sticking)
                    if (height > WATER_LEVEL - 2) {
                        // For shallow areas, raise them slightly above water to prevent sticking
                        height = WATER_LEVEL + 1;
                    } else {
                        // For deeper areas, make them proper water channels
                        height = WATER_LEVEL - 2;
                    }
                }
                break;
        }
        
        // Apply edge details and cliffs with moderate heights
        if (biome === BIOME_CLIFF && distance < 0.9) {
            const cliffHeight = 15 + noise2D(worldX * 0.05, worldZ * 0.05) * 10;
            height = Math.max(height, WATER_LEVEL + cliffHeight);
        }
        
        // Ensure clean water boundary for all islands
        if (distance > 0.85) {
            const edgeFactor = Math.min(1, (distance - 0.85) / 0.15);
            
            // For shore biome, create a smoother transition to water
            if (biome === BIOME_SHORE) {
                const shoreNoise = noise2D(worldX * 0.1, worldZ * 0.1) * 3;
                const targetHeight = WATER_LEVEL + 1 + shoreNoise;
                height = height * (1 - edgeFactor) + targetHeight * edgeFactor;
            }
            
            // Ensure ocean is deep enough
            if (distance > 0.95) {
                const oceanFactor = Math.min(1, (distance - 0.95) / 0.05);
                const oceanDepth = WATER_LEVEL - 5 - oceanFactor * 5;
                height = height * (1 - oceanFactor) + oceanDepth * oceanFactor;
            }
        }
        
        // Ensure minimum height to prevent holes
        const minHeight = biome === BIOME_DEEP_OCEAN ? WATER_LEVEL - 10 : 
                          biome === BIOME_OCEAN ? WATER_LEVEL - 5 : 
                          WATER_LEVEL - 0.5;
        height = Math.max(height, minHeight);
        
        return height;
    }

    calculateBaseHeight(wx, wz, biome, islandShape) {
        // Multiple noise octaves for more natural terrain
        const baseNoise = ridgedNoise(wx * 0.001, wz * 0.001, 4, 0.5, 1) * 0.5;
        const mediumNoise = ridgedNoise(wx * 0.005, wz * 0.005, 3, 0.6, 1) * 0.3;
        const detailNoise = ridgedNoise(wx * 0.02, wz * 0.02, 2, 0.7, 1) * 0.1;
        
        // Combine noise with increased height variation
        // Using settings.amplitude directly from constants
        let height = (baseNoise + mediumNoise + detailNoise) * this.settings.amplitude * 2.5;
        
        // Add terraced effect for certain biomes with increased intensity
        if (biome === BIOME_MOUNTAINS || biome === BIOME_VOLCANIC) {
            const terraces = terraceNoise(wx * 0.002, wz * 0.002, 6, 1.2) * 15;
            height += terraces;
        }
        
        // Get mountain influence for natural mountains
        const mountainNoise = ridgedNoise(wx * 0.002 + 2000, wz * 0.002 + 2000, 5, 0.6, 1);
        
        // Apply biome-specific modifications with more dramatic heights
        switch(biome) {
            case BIOME_VOLCANIC:
                // Volcanic peaks - higher and more dramatic
                const peakHeight = 40 + mountainNoise * 30;
                const distFromCenter = Math.sqrt(wx*wx + wz*wz) / (ISLAND_RADIUS);
                const peakFactor = Math.max(0, 1 - distFromCenter / 0.3);
                height = WATER_LEVEL + peakHeight * peakFactor;
                
                // Add occasional secondary peaks
                if (mountainNoise > 0.7) {
                    height += 25 + mountainNoise * 25;
                }
                break;
                
            case BIOME_MOUNTAINS:
                // Dramatic mountains with increased heights
                const mountainHeight = 30 + mountainNoise * 25;
                const mountainFactor = Math.pow(mountainNoise, 1.2);
                height = BASE_HEIGHT + 10 + mountainHeight * mountainFactor;
                
                // Add ridge details
                height += noise2D(wx * 0.05, wz * 0.05) * 8;
                break;
                
            case BIOME_HILLS:
                // Rolling hills with moderate slopes
                const hillBase = BASE_HEIGHT + 6;
                height = hillBase + height * 0.6;
                // Add occasional higher hills
                const hillDetail = noise2D(wx * 0.02, wz * 0.02);
                if (hillDetail > 0.7) {
                    height += 12 * (hillDetail - 0.7) / 0.3;
                }
                break;
                
            case BIOME_PLAINS:
                // Gently rolling lowlands with subtle variation
                const plainBase = BASE_HEIGHT + 3;
                height = plainBase + height * 0.3;
                // Add subtle terrain variations
                height += noise2D(wx * 0.08, wz * 0.08) * 3;
                break;
                
            case BIOME_SHORE:
                // Beaches and coastal areas
                height = WATER_LEVEL + 1.5 + noise2D(wx * 0.1, wz * 0.1) * 2;
                break;
                
            case BIOME_CLIFF:
                // Coastal cliffs with increased height
                height = WATER_LEVEL + 10 + noise2D(wx * 0.05, wz * 0.05) * 8;
                break;
                
            case BIOME_OCEAN:
                // Ocean floor - ensure it's below water level
                height = WATER_LEVEL - 5 + noise2D(wx * 0.05, wz * 0.05) * 2;
                break;
                
            case BIOME_DEEP_OCEAN:
                // Deep ocean trenches - ensure proper depth
                height = WATER_LEVEL - 10 + noise2D(wx * 0.03, wz * 0.03) * 3;
                break;
        }
        
        return height;
    }

    applyIslandShape(height, distance) {
        // Add subtle noise to the island edge
        const edgeNoise = noise2D(distance * 8, 0) * 0.015; // Reduced from 0.02 for smoother edges
        const adjustedDistance = distance + edgeNoise;
        
        // Get world coordinates for noise variation
        const wx = this.position.x * CHUNK_SIZE * this.settings.frequency;
        const wz = this.position.y * CHUNK_SIZE * this.settings.frequency;
        
        // Account for world boundaries
        const worldX = this.position.x * CHUNK_SIZE - MAP_RADIUS;
        const worldZ = this.position.y * CHUNK_SIZE - MAP_RADIUS;
        const worldEdgeFactor = Math.max(
            Math.abs(worldX / ISLAND_RADIUS),
            Math.abs(worldZ / ISLAND_RADIUS)
        );
        const boundaryAdjustedDistance = adjustedDistance * (1 + worldEdgeFactor * 0.08); // Reduced from 0.1
        
        // Create smooth transitions between biomes
        if (boundaryAdjustedDistance > 1.0) {
            // Ocean floor with gradual slope - less extreme depths
            const oceanDepth = boundaryAdjustedDistance > 1.6 ? 
                WATER_LEVEL - 6 :  // Reduced from -8
                WATER_LEVEL - 4;   // Reduced from -5
            
            height = oceanDepth + ((1.0 - Math.min(boundaryAdjustedDistance, 1.0)) * 1.5); // Reduced from 2
        } else {
            // Calculate shore influence with wider transition zone
            const shoreStart = 0.82; // Adjusted from 0.80 for slightly wider shore
            const shoreWidth = 0.18; // Adjusted from 0.20 for more controlled transition
            const shoreInfluence = Math.max(0, Math.min(1, (boundaryAdjustedDistance - shoreStart) / shoreWidth));
            
            // Base height for shore areas
            const shoreHeight = WATER_LEVEL + 1.2; // Reduced from 1.5 for smoother transition
            
            if (shoreInfluence > 0) {
                // Smooth transition from water to shore using improved easing
                const t = shoreInfluence * shoreInfluence * (3 - 2 * shoreInfluence);
                
                // Add gentle shore variations with reduced amplitude
                const shoreDetail = noise2D(wx * 15 + worldX * 0.05, wz * 15 + worldZ * 0.05) * 0.8; // Reduced from 1.0
                const shoreVariation = Math.max(0, shoreDetail * (1 - shoreInfluence));
                
                // Blend between water level and terrain height with better easing
                const blendedHeight = height * (1 - t) + (shoreHeight + shoreVariation) * t;
                height = Math.max(WATER_LEVEL - 0.8, Math.min(blendedHeight, shoreHeight + 1.5)); // Reduced max height difference
            }
            
            // Add subtle terrain variation that increases away from shore
            const inlandFactor = Math.max(0, 1 - boundaryAdjustedDistance);
            const terrainVariation = noise2D(wx * 10 + distance * 8, wz * 10) * 1.2; // Reduced from 1.5
            height += terrainVariation * Math.pow(inlandFactor, 2) * 0.4; // Reduced from 0.5
        }
        
        return height;
    }

    generateVoxels(heightMap) {
        this.voxels.clear();

        // Clear any existing collision data for this chunk completely
        const chunkWorldX = this.position.x * CHUNK_SIZE - MAP_RADIUS;
        const chunkWorldZ = this.position.y * CHUNK_SIZE - MAP_RADIUS;

        // Get or create collision manager
        if (!window.terrainCollision) {
            window.terrainCollision = new TerrainCollisionManager();
        }

        // First, completely clear collision data for this chunk's area
        for (let x = 0; x < CHUNK_SIZE; x++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
                const absoluteX = this.position.x * CHUNK_SIZE + x;
                const absoluteZ = this.position.y * CHUNK_SIZE + z;
                // Clear all collision data in the vertical column
                window.terrainCollision.clearBlockColumn(absoluteX, absoluteZ);
            }
        }

        // Now generate new terrain and collision data
        for (let x = 0; x < CHUNK_SIZE; x++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
                const height = Math.ceil(heightMap[x][z]);
                const worldX = chunkWorldX + x;
                const worldZ = chunkWorldZ + z;
                const distanceFromCenter = Math.sqrt(worldX * worldX + worldZ * worldZ);
                const maxRadius = ISLAND_RADIUS;
                const distance = distanceFromCenter / maxRadius;
                
                const wx = (x + this.position.x * CHUNK_SIZE) * this.settings.frequency;
                const wz = (z + this.position.y * CHUNK_SIZE) * this.settings.frequency;
                
                const biome = this.getBiome(distance, height, wx, wz);

                const absoluteX = this.position.x * CHUNK_SIZE + x;
                const absoluteZ = this.position.y * CHUNK_SIZE + z;
                
                // Set a minimum depth for solid terrain
                const minY = Math.min(-8, Math.floor(height) - 8);
                const maxY = Math.max(height, 0);

                // Fill ALL blocks from bottom to top with collision data
                for (let y = minY; y <= maxY; y++) {
                    let material = 'stone';
                    let isCollidable = true; // Track if this block should have collision

                    // Surface and near-surface materials
                    if (y === Math.floor(maxY)) {
                        if (biome === BIOME_MOUNTAINS) {
                            material = y > 15 ? 'stone' : 'grass';
                        } else if (biome === BIOME_SHORE) {
                            material = 'sand';
                        } else {
                            material = 'grass';
                        }
                    } else if (y > maxY - 3 && y < maxY) {
                        if (biome === BIOME_MOUNTAINS && y > 13) {
                            material = 'stone';
                        } else if (biome === BIOME_SHORE) {
                            material = 'sand';
                        } else {
                            material = 'dirt';
                        }
                    }

                    // Set the voxel and ensure collision data is set
                    if (isCollidable) {
                        this.voxels.set(`${x},${y},${z}`, material);
                        // Set collision data for each solid block
                        window.terrainCollision.setBlockHeight(absoluteX, absoluteZ, y);
                    }
                }

                // Add water above terrain if below water level
                if (maxY < WATER_LEVEL) {
                    for (let y = maxY + 1; y <= WATER_LEVEL; y++) {
                        this.voxels.set(`${x},${y},${z}`, 'water');
                        // Water blocks don't need collision data
                    }
                }

                // Ensure final collision height is set correctly
                window.terrainCollision.setBlockHeight(absoluteX, absoluteZ, Math.max(height, minY));
            }
        }

        this.generateMesh();
    }

    getBiome(distance, height, wx, wz) {
        const worldX = wx / this.settings.frequency;
        const worldZ = wz / this.settings.frequency;
        return getBiomeAt(worldX, worldZ, height, distance, [], this.settings.frequency);
    }

    generateMesh() {
        try {
            const instances = new Map();
            this.voxels.forEach((type, key) => {
                if (!instances.has(type)) { instances.set(type, []); }
                const [x, y, z] = key.split(',').map(Number);
                if (this.isVoxelExposed(x, y, z)) {
                    const matrix = new THREE.Matrix4().setPosition(
                        x + this.position.x * CHUNK_SIZE,
                        y,
                        z + this.position.y * CHUNK_SIZE
                    );
                    instances.get(type).push(matrix);
                }
            });
            this.mesh = new THREE.Group();
            instances.forEach((matrices, type) => {
                if (matrices.length === 0) return;
                const mesh = new THREE.InstancedMesh(
                    VOXEL_GEOMETRY,
                    this.materials[type],
                    matrices.length
                );
                matrices.forEach((matrix, i) => mesh.setMatrixAt(i, matrix));
                this.mesh.add(mesh);
            });
            if (this.shouldHaveWater()) {
                this.addWaterMesh();
            }
            return this.mesh;
        } catch (e) {
            console.error(`Chunk (${this.position.x},${this.position.y}) failed to generate:`, e);
            const fallbackGeo = new THREE.PlaneGeometry(CHUNK_SIZE, CHUNK_SIZE);
            fallbackGeo.rotateX(-Math.PI / 2);
            this.mesh = new THREE.Mesh(
                fallbackGeo,
                new THREE.MeshStandardMaterial({ color: 'red' })
            );
            return this.mesh;
        }
    }

    createPhysics(heightMap) {
        // Create heightfield physics shape for ground
        const heightfieldData = [];
        for (let i = 0; i <= CHUNK_SIZE; i++) {
            heightfieldData[i] = [];
            for (let j = 0; j <= CHUNK_SIZE; j++) {
                // Ensure height is never negative
                heightfieldData[i][j] = Math.max(0, heightMap[i][j]);
            }
        }

        // Create compound body to hold both heightfield and wall colliders
        const chunkBody = new CANNON.Body({
            mass: 0, // Static body
            position: new CANNON.Vec3(
                this.position.x * CHUNK_SIZE,
                0,
                this.position.y * CHUNK_SIZE
            )
        });

        // Add heightfield shape
        const heightfieldShape = new CANNON.Heightfield(heightfieldData, {
            elementSize: 1 // Distance between heights
        });
        
        // Rotate heightfield to align with Three.js coordinates
        const heightfieldQuat = new CANNON.Quaternion();
        heightfieldQuat.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
        chunkBody.addShape(heightfieldShape, new CANNON.Vec3(0, 0, 0), heightfieldQuat);

        // Add wall colliders where there are steep height differences
        for (let x = 0; x < CHUNK_SIZE; x++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
                const currentHeight = heightMap[x][z];
                
                // Check adjacent blocks for steep height differences
                const checks = [
                    { dx: 1, dz: 0 },
                    { dx: -1, dz: 0 },
                    { dx: 0, dz: 1 },
                    { dx: 0, dz: -1 }
                ];

                for (const check of checks) {
                    const nx = x + check.dx;
                    const nz = z + check.dz;
                    
                    // Skip if outside chunk bounds
                    if (nx < 0 || nx >= CHUNK_SIZE || nz < 0 || nz >= CHUNK_SIZE) continue;
                    
                    const neighborHeight = heightMap[nx][nz];
                    const heightDiff = Math.abs(currentHeight - neighborHeight);
                    
                    // If height difference is significant (more than 1 block), add a wall collider
                    if (heightDiff > 1) {
                        const minHeight = Math.min(currentHeight, neighborHeight);
                        const wallHeight = heightDiff;
                        
                        // Create box shape for wall
                        // Make it slightly thicker (0.1) than visual blocks for better collision
                        const wallShape = new CANNON.Box(new CANNON.Vec3(
                            check.dx !== 0 ? 0.1 : 0.5,  // x
                            wallHeight / 2,              // y (half-height because CANNON uses half-extents)
                            check.dz !== 0 ? 0.1 : 0.5   // z
                        ));
                        
                        // Position the wall collider
                        const wallPos = new CANNON.Vec3(
                            x + 0.5 + (check.dx * 0.5),  // Center of block + offset
                            minHeight + (wallHeight / 2), // Middle of wall height
                            z + 0.5 + (check.dz * 0.5)   // Center of block + offset
                        );
                        
                        // Add wall shape to compound body
                        chunkBody.addShape(wallShape, wallPos);
                    }
                }
            }
        }

        this.physicsBody = chunkBody;
        window.physicsWorld.addBody(chunkBody);
    }

    dispose() {
        if (this.physicsBody) {
            window.physicsWorld.removeBody(this.physicsBody);
        }
    }

    // Helper methods
    isVoxelExposed(x, y, z) {
        return !this.voxels.has(`${x},${y+1},${z}`) ||
               !this.voxels.has(`${x},${y-1},${z}`) ||
               !this.voxels.has(`${x+1},${y},${z}`) ||
               !this.voxels.has(`${x-1},${y},${z}`) ||
               !this.voxels.has(`${x},${y},${z+1}`) ||
               !this.voxels.has(`${x},${y},${z-1}`);
    }

    shouldHaveWater() {
        const worldPosX = this.position.x * CHUNK_SIZE - MAP_RADIUS;
        const worldPosZ = this.position.y * CHUNK_SIZE - MAP_RADIUS;
        const distanceFromCenter = Math.sqrt(worldPosX * worldPosX + worldPosZ * worldPosZ);
        
        // Create water for most of the world, except the very center of the island
        return distanceFromCenter > ISLAND_RADIUS * 0.6;
    }

    addWaterMesh() {
        const water = new THREE.Mesh(WATER_GEOMETRY, this.materials.water);
        water.rotation.x = -Math.PI / 2;
        water.position.set(
            this.position.x * CHUNK_SIZE + CHUNK_SIZE / 2,
            WATER_LEVEL,
            this.position.y * CHUNK_SIZE + CHUNK_SIZE / 2
        );
        this.mesh.add(water);
    }

    getHeightAt(localX, localZ) {
        if (localX < 0 || localX >= CHUNK_SIZE || localZ < 0 || localZ >= CHUNK_SIZE) {
            return WATER_LEVEL;
        }

        // Find highest solid block
        for (let y = CHUNK_SIZE - 1; y >= 0; y--) {
            const key = `${localX},${y},${localZ}`;
            if (this.voxels.has(key)) {
                const blockType = this.voxels.get(key);
                // Return surface height plus small offset
                return y + 1.1;
            }
        }
        return WATER_LEVEL;
    }

    // Add a new method to limit steep slopes for walkable terrain
    limitSteepSlopes(heightMap) {
        // Safety check for invalid heightMap
        if (!heightMap || !Array.isArray(heightMap) || heightMap.length === 0) {
            console.warn('Invalid heightMap provided to limitSteepSlopes');
            return createHeightMap(CHUNK_SIZE, WATER_LEVEL);
        }
        
        // Increased from 2.5 to allow for more dramatic terrain but keep it walkable
        const maxSlopeDelta = 1.8; 
        
        // Create a safe copy of the height map with validation
        const result = [];
        for (let x = 0; x < heightMap.length; x++) {
            result[x] = [];
            for (let z = 0; z < (heightMap[x] ? heightMap[x].length : 0); z++) {
                result[x][z] = isFinite(heightMap[x][z]) ? heightMap[x][z] : WATER_LEVEL;
            }
        }
        
        // Apply slope limiting with error handling - multiple passes for better results
        for (let pass = 0; pass < 3; pass++) {
            for (let x = 1; x < Math.min(CHUNK_SIZE, result.length); x++) {
                for (let z = 1; z < Math.min(CHUNK_SIZE, result[x] ? result[x].length : 0); z++) {
                    try {
                        // Get current and neighboring heights with safety checks
                        const currentHeight = result[x][z];
                        const neighbors = [];
                        
                        // Safely add neighbors only if they exist
                        if (x > 0 && result[x-1] && typeof result[x-1][z] === 'number')
                            neighbors.push(result[x-1][z]); // West
                            
                        if (x+1 < result.length && result[x+1] && typeof result[x+1][z] === 'number')
                            neighbors.push(result[x+1][z]); // East
                            
                        if (result[x] && typeof result[x][z-1] === 'number')
                            neighbors.push(result[x][z-1]); // North
                            
                        if (result[x] && typeof result[x][z+1] === 'number')
                            neighbors.push(result[x][z+1]); // South
                        
                        // Also check diagonal neighbors for smoother transitions
                        if (x > 0 && z > 0 && result[x-1] && typeof result[x-1][z-1] === 'number')
                            neighbors.push(result[x-1][z-1]); // Northwest
                            
                        if (x+1 < result.length && z > 0 && result[x+1] && typeof result[x+1][z-1] === 'number')
                            neighbors.push(result[x+1][z-1]); // Northeast
                            
                        if (x > 0 && z+1 < (result[x] ? result[x].length : 0) && result[x-1] && typeof result[x-1][z+1] === 'number')
                            neighbors.push(result[x-1][z+1]); // Southwest
                            
                        if (x+1 < result.length && z+1 < (result[x] ? result[x].length : 0) && result[x+1] && typeof result[x+1][z+1] === 'number')
                            neighbors.push(result[x+1][z+1]); // Southeast
                        
                        // Only proceed if we have neighbors to compare with
                        if (neighbors.length > 0) {
                            // Use a dynamic slope limitation based on absolute height
                            // This allows steeper slopes at higher elevations (mountains)
                            // but keeps gentler slopes near sea level (beaches)
                            
                            // Get biome to adjust slope based on terrain type
                            const worldX = this.position.x * CHUNK_SIZE + x;
                            const worldZ = this.position.y * CHUNK_SIZE + z;
                            const distanceFromCenter = Math.sqrt(worldX * worldX + worldZ * worldZ) / ISLAND_RADIUS;
                            const biome = this.getBiome(distanceFromCenter, currentHeight, worldX, worldZ);
                            
                            // Check if this is an archipelago island shape - need extra slope limiting
                            const worldSeed = window.worldSeed;
                            const islandShape = getIslandShape(worldSeed);
                            const isArchipelago = islandShape === ISLAND_SHAPES.ARCHIPELAGO;
                            
                            // Set max slope based on biome and island shape
                            let biomeSlopeFactor = 1.0;
                            if (biome === BIOME_MOUNTAINS || biome === BIOME_VOLCANIC) {
                                biomeSlopeFactor = 1.4; // Allow steeper slopes in mountains
                            } else if (biome === BIOME_HILLS) {
                                biomeSlopeFactor = 1.2; // Moderate slopes in hills
                            } else if (biome === BIOME_SHORE) {
                                biomeSlopeFactor = 0.8; // Gentler slopes on shores
                            }
                            
                            // Archipelago islands need even gentler slopes in depression areas
                            if (isArchipelago) {
                                // Check if we're in a potential depression area
                                const depressionCheck = noise2D(worldX * 0.01 + 1500, worldZ * 0.01 + 1500);
                                if (depressionCheck > 0.4 && depressionCheck < 0.6) {
                                    biomeSlopeFactor *= 0.7; // Much gentler slopes in depression areas
                                } else {
                                    biomeSlopeFactor *= 0.9; // Slightly gentler slopes everywhere on archipelago
                                }
                            }
                            
                            const heightBasedDelta = maxSlopeDelta * biomeSlopeFactor * (1 + Math.min(0.5, (currentHeight - WATER_LEVEL) / 30));
                            const maxAllowedDelta = Math.min(heightBasedDelta, 3.0); // Cap maximum slope delta
                            
                            // Calculate average neighbor height for smoother transitions
                            const avgNeighborHeight = neighbors.reduce((sum, h) => sum + h, 0) / neighbors.length;
                            
                            // Check if any slopes are too steep
                            for (const neighborHeight of neighbors) {
                                const heightDiff = Math.abs(currentHeight - neighborHeight);
                                
                                // If slope is too steep, adjust the current height
                                if (heightDiff > maxAllowedDelta) {
                                    // Moving height toward neighbor proportionally to how extreme the slope is
                                    const adjustmentFactor = 0.5; // Less aggressive slope reduction
                                    
                                    if (currentHeight > neighborHeight) {
                                        result[x][z] = currentHeight - (heightDiff - maxAllowedDelta) * adjustmentFactor;
                                    } else {
                                        result[x][z] = currentHeight + (heightDiff - maxAllowedDelta) * adjustmentFactor;
                                    }
                                    
                                    // Also gently pull toward average for smoother overall terrain
                                    result[x][z] = result[x][z] * 0.85 + avgNeighborHeight * 0.15;
                                }
                            }
                        }
                    } catch (error) {
                        console.warn(`Error in slope calculation at [${x}][${z}]: ${error.message}`);
                        // Skip this cell if there's an error
                    }
                }
            }
        }
        
        return result;
    }

    // Add a method to smooth chunk boundaries specifically
    smoothChunkBoundaries(heightMap) {
        // Safety check for invalid heightMap
        if (!heightMap || !Array.isArray(heightMap) || heightMap.length === 0) {
            console.warn('Invalid heightMap provided to smoothChunkBoundaries');
            return heightMap;
        }
        
        const result = [...heightMap.map(row => [...row])]; // Create a copy
        const mapSize = heightMap.length;
        
        // Focus on edge regions for stronger smoothing
        for (let pass = 0; pass < 2; pass++) {
            for (let x = 0; x < mapSize; x++) {
                for (let z = 0; z < mapSize; z++) {
                    // Skip interior cells - focus only on the margin areas
                    const isEdgeRegion = 
                        x < CHUNK_BLEND_MARGIN * 2 || 
                        x >= mapSize - CHUNK_BLEND_MARGIN * 2 || 
                        z < CHUNK_BLEND_MARGIN * 2 || 
                        z >= mapSize - CHUNK_BLEND_MARGIN * 2;
                    
                    if (!isEdgeRegion) continue;
                    
                    // Use a larger sampling radius for edges
                    const radius = 3;
                    let sum = 0;
                    let weight = 0;
                    
                    // Weighted gaussian blur with stronger weights in boundary direction
                    for (let nx = Math.max(0, x - radius); nx <= Math.min(mapSize - 1, x + radius); nx++) {
                        for (let nz = Math.max(0, z - radius); nz <= Math.min(mapSize - 1, z + radius); nz++) {
                            // Calculate distance from center
                            const dist = Math.sqrt((nx - x) ** 2 + (nz - z) ** 2);
                            if (dist > radius) continue;
                            
                            // Calculate direction weight - higher for points toward chunk center
                            const dirX = nx < x ? -1 : (nx > x ? 1 : 0);
                            const dirZ = nz < z ? -1 : (nz > z ? 1 : 0);
                            
                            // Check if direction points toward center
                            const isTowardCenter = 
                                (x < CHUNK_BLEND_MARGIN * 2 && dirX > 0) ||
                                (x >= mapSize - CHUNK_BLEND_MARGIN * 2 && dirX < 0) ||
                                (z < CHUNK_BLEND_MARGIN * 2 && dirZ > 0) ||
                                (z >= mapSize - CHUNK_BLEND_MARGIN * 2 && dirZ < 0);
                            
                            // Higher weight for points toward center
                            const dirWeight = isTowardCenter ? 1.5 : 1.0;
                            
                            // Gaussian weight
                            const gaussWeight = Math.exp(-(dist * dist) / (radius * 0.8));
                            
                            // Combined weight
                            const cellWeight = gaussWeight * dirWeight;
                            
                            // Add to weighted sum
                            if (heightMap[nx] && typeof heightMap[nx][nz] === 'number') {
                                sum += heightMap[nx][nz] * cellWeight;
                                weight += cellWeight;
                            }
                        }
                    }
                    
                    // Calculate final smoothed height
                    if (weight > 0) {
                        const smoothedHeight = sum / weight;
                        
                        // Apply smoothing with stronger effect near the absolute edges
                        const edgeDist = Math.min(
                            x, mapSize - 1 - x,
                            z, mapSize - 1 - z
                        );
                        
                        // Calculate blend factor based on proximity to edge
                        const edgeBlend = Math.max(0, Math.min(1, edgeDist / (CHUNK_BLEND_MARGIN * 1.5)));
                        const blendFactor = 0.8 - 0.7 * edgeBlend; // Stronger at edges, weaker inward
                        
                        // Apply blended height
                        result[x][z] = result[x][z] * (1 - blendFactor) + smoothedHeight * blendFactor;
                    }
                }
            }
            
            // Use the result of the first pass for the second pass
            if (pass === 0) {
                for (let x = 0; x < mapSize; x++) {
                    for (let z = 0; z < mapSize; z++) {
                        heightMap[x][z] = result[x][z];
                    }
                }
            }
        }
        
        return result;
    }

    // Add method to blend with neighbor height data for chunk boundaries
    blendWithNeighbors(heightMap) {
        if (!this.neighborHeightData) {
            return heightMap; // No neighbors to blend with
        }
        
        // Create a copy of the heightmap to modify
        const result = [...heightMap.map(row => [...row])];
        const size = heightMap.length;
        
        // Border thickness to blend - just the outer edge cells
        const borderWidth = 2;
        
        // Function to blend a border with a neighbor
        const blendBorder = (neighbor, edgeX, edgeZ, dirX, dirZ) => {
            if (!neighbor) return;
            
            // Iterate over the border cells
            for (let i = 0; i < CHUNK_SIZE; i++) {
                for (let border = 0; border < borderWidth; border++) {
                    // Calculate positions based on direction
                    let x, z;
                    if (dirX > 0) x = size - 1 - border;
                    else if (dirX < 0) x = border;
                    else x = i;
                    
                    if (dirZ > 0) z = size - 1 - border;
                    else if (dirZ < 0) z = border;
                    else z = i;
                    
                    // Get matching position from neighbor
                    let nx, nz;
                    if (dirX > 0) nx = border;
                    else if (dirX < 0) nx = size - 1 - border;
                    else nx = i;
                    
                    if (dirZ > 0) nz = border;
                    else if (dirZ < 0) nz = size - 1 - border;
                    else nz = i;
                    
                    // Get heights from both chunks
                    try {
                        const ourHeight = heightMap[x][z];
                        const theirHeight = neighbor[nx][nz];
                        
                        if (isFinite(ourHeight) && isFinite(theirHeight)) {
                            // Blend with weight based on distance from edge
                            const weight = 1 - (border / borderWidth);
                            result[x][z] = (1 - weight) * ourHeight + weight * theirHeight;
                        }
                    } catch (e) {
                        // Just ignore any errors - we're just trying to improve blending
                        console.warn('Error during border blending, continuing without this cell');
                    }
                }
            }
        };
        
        // Blend with each neighbor if available
        if (this.neighborHeightData.north) {
            blendBorder(this.neighborHeightData.north, 0, 0, 0, -1);
        }
        if (this.neighborHeightData.south) {
            blendBorder(this.neighborHeightData.south, 0, size-1, 0, 1);
        }
        if (this.neighborHeightData.east) {
            blendBorder(this.neighborHeightData.east, size-1, 0, 1, 0);
        }
        if (this.neighborHeightData.west) {
            blendBorder(this.neighborHeightData.west, 0, 0, -1, 0);
        }
        
        // Blend corners if we have diagonal neighbors
        if (this.neighborHeightData.northeast) {
            // Just the corner cells
            const cornerSize = 2;
            for (let dx = 0; dx < cornerSize; dx++) {
                for (let dz = 0; dz < cornerSize; dz++) {
                    try {
                        const x = size - 1 - dx;
                        const z = dz;
                        const nx = dx;
                        const nz = size - 1 - dz;
                        
                        const ourHeight = heightMap[x][z];
                        const theirHeight = this.neighborHeightData.northeast[nx][nz];
                        
                        if (isFinite(ourHeight) && isFinite(theirHeight)) {
                            // Stronger weight in the exact corner
                            const weight = 1 - (Math.sqrt(dx*dx + dz*dz) / (cornerSize * 1.5));
                            result[x][z] = (1 - weight) * ourHeight + weight * theirHeight;
                        }
                    } catch (e) {
                        // Ignore errors in corner blending
                    }
                }
            }
        }
        
        // Similar for other corners...
        
        return result;
    }
}
